import React, { useRef, useState, useEffect } from "react";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Paper, Chip, IconButton } from "@material-ui/core";
import Input from "../Input";

export default function SearchBar({
  onSearch,
  keyMap,
  placeholder,
  clearSearch,
  backgroundcolor = "#ffffff",
  chipBackgroundColor = "#e3f2fd",
  chipColor = "#1976d2",
  elevation = 1,
  clearable,
  defaultKey = null,
  defaultValue = [],
  focused,
  setFocused,
}) {
  const [search, setSearch] = useState(defaultValue);
  const [keySelected, setKeySelected] = useState(defaultKey);
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const resetSearch = () => {
    clearSearch();
    setSearch([]);
  };

  useEffect(() => {
    if (focused !== undefined) {
      focused && input.current.focus();
    }
  }, [focused]);

  useEffect(() => {
    if (defaultKey && defaultValue) {
      setKeySelected(null);
      let _search = [...search];
      const last = _search.pop();
      _search = [..._search, { key: last.key, value: defaultValue[0].value }];
      setSearch(_search);
    }
    // eslint-disable-next-line
  }, []);

  const input = useRef(null);

  const selectKey = (value) => {
    if (keySelected) {
      setKeySelected(null);
      let _search = [...search];
      const last = _search.pop();
      _search = [..._search, { key: last.key, value }];
      setSearch(_search);
      onSearch(
        _search.reduce((acc, { key, value }) => {
          acc[key] = value;
          return acc;
        }, {})
      );
    } else {
      setKeySelected(value);
      setSearch([...search, { key: value }]);
    }
    if (input.current) {
      input.current.blur();
      requestAnimationFrame(() => input.current && input.current.focus());
    }
  };

  const handleKeyDown = (event) => {
    const backspace = { keyCode: 8 };

    if (event.keyCode === backspace.keyCode && event.target.value === "") {
      if (keySelected === null) {
        const _search = search.slice(0, -1);

        setSearch(_search);
      } else {
        setKeySelected(null);
      }
    }
  };

  const handleInputChange = (_, value) => {
    if (keyMap[value] || (keySelected && keyMap[keySelected].includes(value))) {
      selectKey(value);
    }
  };

  const handleChange = (_, [value], reason) => {
    if (reason !== "select-option") return;
    selectKey(value);
  };

  const handleRemoveKey = (key, value, search) => {
    if (key === "Tag") {
      return search.filter((i) => i.value !== value);
    } else return search.filter((i) => i.key !== key);
  };

  const handleRemove = (key, value) => () => {
    const _search = handleRemoveKey(key, value, search);
    setSearch(_search);
    if (keySelected) {
      setKeySelected(null);
    } else {
      onSearch(
        _search.reduce((acc, { key, value }) => {
          acc[key] = value;
          return acc;
        }, {}),
        true
      );
    }
  };

  const handleTagsKeyMap = (keyMap = {}, selectedTagValues = []) => {
    if (Object.keys(keyMap).includes("Tag")) {
      return {
        ...keyMap,
        Tag: keyMap.Tag.filter((tag) => !selectedTagValues.includes(tag)),
      };
    } else return keyMap;
  };

  const getOptions = (search = []) => {
    const selected = search
      .filter((item) => (item || {}).key !== "Tag")
      .reduce((acc, i) => {
        acc[i.key] = true;
        return acc;
      }, {});
    // eslint-disable-next-line array-callback-return
    const selectedTagValues = search
      .map((item) => (item.key === "Tag" ? item.value : ""))
      .filter(Boolean);
    if (search.length > 0 && !search[search.length - 1].value) {
      const modifiedKeyMap = handleTagsKeyMap(keyMap, selectedTagValues);
      const options = [
        ...new Set(
          modifiedKeyMap[search[search.length - 1].key]
            .reduce(
              (a, v) => a.concat(typeof v === "number" ? String(v) : v),
              []
            )
            .filter(
              (item) =>
                (typeof item === "string" &&
                  !selectedTagValues.includes(item) &&
                  item.trim() !== "") ||
                Array.isArray(item)
            )
        ),
      ].sort();
      return options;
    }
    return Object.keys(keyMap).filter((k) => {
      return !selected[k];
    });
  };

  const checkForChip = () => {
    return (
      search.find((item) => item.value !== undefined) && keySelected === null
    );
  };

  const handleBlur = () => focused !== undefined && setFocused(false);

  const handleEnterPress = () => {
    if (getOptions(search)[0] === "No option found") return;
    setKeySelected(null);
    let _search = [...search];
    const last = _search.pop();
    _search = [..._search, { key: last.key, value: getOptions(search)[0] }];
    setSearch(_search);
    onSearch(
      _search.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {})
    );
    handleClose();
  };

  // CSS for the component - previously in SearchBar.style.js
  const searchStyles = `
    .search-wrapper {
      border-radius: 2px;
      margin-bottom: 1.25rem;
      display: flex;
      align-items: center;
      background-color: ${backgroundcolor};
    }

    .search-icon {
      margin: 0.75rem;
      color: rgba(0, 0, 0, 0.24);
    }

    .chip-wrapper {
      flex-shrink: 0;
      line-height: 1.5;
      margin-right: 0.8rem;
    }

    .autocomplete {
      flex-grow: 1;
      padding-top: 13px;
      padding-bottom: 13px;
    }

    .search-input {
      border: 0;
      width: 100%;
      height: 100%;
      padding: 0.25rem;
      background-color: white;
      width: 100%;
    }

    .mui-input-wrapper {
      width: 100%;
    }

    .MuiInputBase-input {
      padding: 6px 0 6px;
    }

    .search-input-group {
      border: 0;
      width: 100%;
      height: 100%;
      padding: 0.25rem;
    }

    .chip {
      background-color: ${chipBackgroundColor};
      color: ${chipColor};
      border-radius: 100px;
      height: 80%;
      padding-top: 6px;
      padding-bottom: 6px;
      font-size: 16px;
      margin: 0 0.8rem 0 0;
    }

    .chip .MuiChip-labelSmall {
      font-size: 14px;
    }

    .chip .MuiChip-deleteIcon {
      margin: 3px 6px 2px 0px;
    }

    .MuiChip-deleteIcon {
      margin-top: 2px;
    }
  `;

  return (
    <div className="search-view">
      <style>{searchStyles}</style>
      <Paper className="search-wrapper" elevation={elevation}>
        <SearchIcon className="search-icon" />
        <div className="chip-wrapper">
          {search.map(({ key, value }) => {
            if (!value) {
              return `${key}:`;
            }
            return (
              <Chip
                className="chip"
                key={`${key}-${value}`}
                size="small"
                label={value ? `${key}: ${value}` : key}
                onDelete={handleRemove(key, value)}
              />
            );
          })}
        </div>
        <Autocomplete
          className="autocomplete"
          value={[]}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          multiple
          options={getOptions(search)}
          onChange={handleChange}
          onBlur={handleBlur}
          openOnFocus
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          id="1"
          renderInput={(params) => (
            <div ref={params.InputProps.ref}>
              <Input
                ref={input}
                placeholder={placeholder}
                inputClassName="search-input"
                className="mui-input-wrapper"
                inputProps={params.inputProps}
                background="transparent"
                id="1"
                variant="naked"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    handleEnterPress();
                  }
                }}
              />
            </div>
          )}
        />
        {checkForChip() && (
          <IconButton onClick={resetSearch}>
            <ClearIcon />
          </IconButton>
        )}
      </Paper>
    </div>
  );
}
